import {
  IDEA_FREQUENCY_VALUES,
  IDEA_IMPACT_VALUES,
  IDEA_REACH_VALUES,
  type IdeaFrequency,
  type IdeaImpact,
  type IdeaReach,
} from '@cult/domain';
import type { AttachmentInput } from '@cult/schemas';
import { z } from 'zod';

const STORAGE_KEY = 'cult:idea-draft';
const SCHEMA_VERSION = 1;

const AttachmentShape = z.object({
  url: z.string(),
  filename: z.string(),
  mime: z.string(),
  size: z.number(),
});

const IdeaDraftSchema = z.object({
  v: z.literal(SCHEMA_VERSION),
  title: z.string(),
  // Drafts saved before the form split the description have body; it comes back as the problem.
  body: z.string().optional(),
  problem: z.string().optional(),
  solution: z.string().optional(),
  categoryId: z.string().nullable(),
  // Optional: drafts saved before the field existed have none.
  frequency: z
    .number()
    .refine((v): v is IdeaFrequency => (IDEA_FREQUENCY_VALUES as number[]).includes(v))
    .nullable()
    .optional(),
  reach: z
    .number()
    .refine((v): v is IdeaReach => (IDEA_REACH_VALUES as number[]).includes(v))
    .nullable()
    .optional(),
  impact: z
    .number()
    .refine((v): v is IdeaImpact => (IDEA_IMPACT_VALUES as number[]).includes(v))
    .nullable()
    .optional(),
  attachments: z.array(AttachmentShape),
  updatedAt: z.string(),
});

export type IdeaDraftSnapshot = {
  title: string;
  problem: string;
  solution: string;
  categoryId: string | null;
  frequency?: IdeaFrequency | null;
  reach?: IdeaReach | null;
  impact?: IdeaImpact | null;
  attachments: AttachmentInput[];
};

export type IdeaDraft = IdeaDraftSnapshot & { v: number; updatedAt: string };

/** Read a previously-saved draft or return `null` if missing/invalid. */
export function readDraft(): IdeaDraft | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = IdeaDraftSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) return null;
    const { body, problem, solution, ...rest } = parsed.data;
    return { ...rest, problem: problem ?? body ?? '', solution: solution ?? '' };
  } catch {
    return null;
  }
}

export function writeDraft(snapshot: IdeaDraftSnapshot): void {
  if (typeof window === 'undefined') return;
  const payload: IdeaDraft = {
    v: SCHEMA_VERSION,
    title: snapshot.title,
    problem: snapshot.problem,
    solution: snapshot.solution,
    categoryId: snapshot.categoryId,
    frequency: snapshot.frequency ?? null,
    reach: snapshot.reach ?? null,
    impact: snapshot.impact ?? null,
    attachments: snapshot.attachments,
    updatedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota or privacy mode — silently drop. The leave guard still protects users.
  }
}

export function clearDraft(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * True when the draft has at least one field worth surfacing. Used to
 * decide whether to render the restored-draft banner.
 */
export function isDraftMeaningful(draft: IdeaDraftSnapshot): boolean {
  if (draft.title.trim().length > 0) return true;
  if (draft.problem.trim().length > 0) return true;
  if (draft.solution.trim().length > 0) return true;
  if (draft.categoryId !== null && draft.categoryId.length > 0) return true;
  if (draft.frequency != null) return true;
  if (draft.reach != null) return true;
  if (draft.impact != null) return true;
  if (draft.attachments.length > 0) return true;
  return false;
}
